// The viewer's participation in one occurrence (mirrors iOS
// OccurrenceParticipation). EventOccurrenceSerializer resolves it per
// occurrence:
//   my_rsvp_status        effective status: their occurrence RSVP's, else
//                         their series RSVP's, else null
//   series_rsvp_status    their series RSVP status (EventSerializer my_rsvp)
//   has_rsvp_override     whether they answered THIS occurrence (false for a
//                         reminder-only occurrence RSVP — not "no participation")
//   my_reminder           effective reminder: override, else series, else "none"
//   my_reminder_override  their stored occurrence reminder; null = inherited
//   access_scope          "series" (creator or series RSVP) or "occurrence"
//                         (invited to this occurrence only: /events/<id>/ is a
//                         404 for them and the series is masked)
//
// The series RSVP (event.my_rsvp) and occurrence RSVPs are independent
// records. Never use an occurrence's effective status as the series RSVP, or
// the series RSVP as an occurrence's.

// eventDomain imports this file, so its isCanceled isn't imported back.
const isCanceled = (occurrence) => occurrence?.status === "canceled";

export const ACCESS_SCOPE = { SERIES: "series", OCCURRENCE: "occurrence" };

// The `reminder` value that clears an occurrence's own reminder (JSON null):
// the series reminder applies again. "none" is different — reminders off here.
export const INHERIT_REMINDER = null;

// Normalized participation, or null when the payload doesn't describe it
// (older payloads without the fields, or every field null — unannotated).
// has_rsvp_override is the usual marker, but a payload that resolved the
// reminder or access scope without it is still kept (no status override)
// rather than dropping that data.
export function occurrenceParticipation(occurrence) {
  if (!occurrence) return null;
  const hasOverride = occurrence.has_rsvp_override;
  const reminder = occurrence.my_reminder ?? null;
  const reminderOverride = occurrence.my_reminder_override ?? null;
  const accessScope = occurrence.access_scope ?? null;
  if (hasOverride == null && accessScope == null && reminder == null && reminderOverride == null) {
    return null;
  }
  return {
    status: occurrence.my_rsvp_status ?? null,
    seriesStatus: occurrence.series_rsvp_status ?? null,
    hasStatusOverride: hasOverride === true,
    reminder,
    reminderOverride,
    hasReminderOverride: reminderOverride != null,
    accessScope,
    isOccurrenceOnly: accessScope === ACCESS_SCOPE.OCCURRENCE,
  };
}

export function isOccurrenceOnly(occurrence) {
  return occurrence?.access_scope === ACCESS_SCOPE.OCCURRENCE;
}

// A series participant declined this date on its own (an explicit occurrence
// RSVP), as opposed to inheriting a declined series RSVP. It stays on Calendar
// looking canceled, so the answer can be changed back. Never true for an
// occurrence-only guest or a canceled occurrence.
export function isDeclinedOnItsOwn(occurrence) {
  const p = occurrenceParticipation(occurrence);
  return (
    Boolean(p) &&
    !isCanceled(occurrence) &&
    p.hasStatusOverride &&
    p.status === "declined" &&
    p.accessScope === ACCESS_SCOPE.SERIES
  );
}

// Drawn like a canceled occurrence: canceled, or declined on its own. Its
// status is never changed to canceled.
export function appearsCanceled(occurrence) {
  return isCanceled(occurrence) || isDeclinedOnItsOwn(occurrence);
}

// POST /event-occurrences/{id}/rsvp/ body. `update` is { status?, reminder? }:
// a key that's absent (or undefined) is left out, so the backend leaves it
// unchanged; reminder null (INHERIT_REMINDER) is sent as an explicit null.
export function occurrenceRsvpBody(update = {}) {
  const body = {};
  if (update.status !== undefined) body.status = update.status;
  if (update.reminder !== undefined) body.reminder = update.reminder;
  return body;
}

// The occurrence after a POST /event-occurrences/{id}/rsvp/ response
// ({ status, reminder, reminder_override, series_status, has_override }). The
// response has no access_scope; it follows the backend's rule: series with a
// series RSVP (or the creator — the previous scope), occurrence with only an
// occurrence status, none without either.
export function applyOccurrenceRsvpState(occurrence, state) {
  const status = state?.status ?? null;
  const seriesStatus = state?.series_status ?? null;
  let accessScope = null;
  if (seriesStatus != null || occurrence?.access_scope === ACCESS_SCOPE.SERIES) {
    accessScope = ACCESS_SCOPE.SERIES;
  } else if (status != null) {
    accessScope = ACCESS_SCOPE.OCCURRENCE;
  }
  return {
    ...occurrence,
    my_rsvp_status: status,
    series_rsvp_status: seriesStatus,
    has_rsvp_override: Boolean(state?.has_override),
    my_reminder: state?.reminder ?? null,
    my_reminder_override: state?.reminder_override ?? null,
    access_scope: accessScope,
  };
}

// The occurrence after a confirmed series RSVP change (`seriesRsvp`: the
// series RSVP response { status, reminder }, or null when it's gone): an
// explicit status and a reminder override are kept; inherited values take the
// series'. Occurrences without resolved participation are returned as they
// are (callers re-read them). Never call with a cached, possibly stale RSVP.
export function rebaseOnSeriesRsvp(occurrence, seriesRsvp) {
  const p = occurrenceParticipation(occurrence);
  if (!p) return occurrence;
  const status = p.hasStatusOverride ? p.status : seriesRsvp?.status ?? null;
  let reminder;
  if (status == null) reminder = null;
  else if (p.reminderOverride != null) reminder = p.reminderOverride;
  else if (seriesRsvp) reminder = seriesRsvp.reminder ?? "none";
  else reminder = "none";
  let accessScope = null;
  if (seriesRsvp || p.accessScope === ACCESS_SCOPE.SERIES) accessScope = ACCESS_SCOPE.SERIES;
  else if (status != null) accessScope = ACCESS_SCOPE.OCCURRENCE;
  return {
    ...occurrence,
    my_rsvp_status: status,
    series_rsvp_status: seriesRsvp?.status ?? null,
    my_reminder: reminder,
    access_scope: accessScope,
  };
}

// Where picking `option` in the detail's one Reminder row is saved (mirrors
// iOS OccurrenceParticipationPresentation.reminderChange):
//   { target: "series", reminder }      POST /events/{id}/rsvp/ {"reminder"}
//   { target: "occurrence", reminder }  POST /event-occurrences/{id}/rsvp/
//                                       {"reminder"} — a value, or null to
//                                       inherit the series reminder again
//   null                                nothing to send (already shown)
// By default a series participant edits the series reminder. A date with its
// own reminder edits that override instead (a series change wouldn't change
// what this date does), and picking the series value clears it. An
// occurrence-only guest — or anyone without a series RSVP but the host — has
// only the occurrence reminder.
//
// `seriesReminder` is the viewer's series reminder (event.my_rsvp.reminder),
// when the full Event is loaded.
export function reminderEdit({ occurrence, option, isOwner = false, seriesReminder = null }) {
  const p = occurrenceParticipation(occurrence);
  if (!p) return null;
  const current = p.reminder ?? "none";
  if (option === current) return null;
  const occurrenceOnly = p.isOccurrenceOnly || (p.seriesStatus == null && !isOwner);
  if (occurrenceOnly) return { target: "occurrence", reminder: option };
  if (!p.hasReminderOverride) return { target: "series", reminder: option };
  if (seriesReminder != null && option === seriesReminder) {
    return { target: "occurrence", reminder: INHERIT_REMINDER };
  }
  return { target: "occurrence", reminder: option };
}

// Calendar visibility by effective participation:
//   going (series or this date), or no status info (older payloads)  shown
//   pending invitation                                                inbox only
//   declined on its own by a series participant                       shown, subdued
//   declined through the series, or an occurrence-only decline        hidden
// Canceled occurrences keep their own (existing) presentation.
export function isCalendarVisible(occurrence) {
  const p = occurrenceParticipation(occurrence);
  if (p && p.status == null) return p.accessScope === ACCESS_SCOPE.SERIES;
  switch (occurrence?.my_rsvp_status ?? null) {
    case null:
    case "going":
      return true;
    case "invited":
      return false;
    case "declined":
      return isDeclinedOnItsOwn(occurrence);
    default:
      return true; // unknown future status: show by default
  }
}

// Home (timeline rows and Next Event) lists what the viewer attends: never a
// canceled occurrence, a pending invitation or a declined one — a date
// declined on its own included, though Calendar still shows it.
export function isHomeEligible(occurrence) {
  if (!occurrence || isCanceled(occurrence)) return false;
  const p = occurrenceParticipation(occurrence);
  if (p && p.status == null) return p.accessScope === ACCESS_SCOPE.SERIES;
  const status = occurrence.my_rsvp_status ?? null;
  return status !== "invited" && status !== "declined";
}

// Under the detail's Reminder row while this date has its own reminder (a
// series participant's override): why a change here doesn't change the other
// dates, and which choice follows the series again. null otherwise — never
// for an occurrence-only guest, who has no series reminder.
// `reminderLabel(value)` turns a reminder value into its display label.
export function reminderFooter({ occurrence, isOwner = false, seriesReminder = null, reminderLabel }) {
  const p = occurrenceParticipation(occurrence);
  if (!p || p.isOccurrenceOnly || (p.seriesStatus == null && !isOwner) || !p.hasReminderOverride) {
    return null;
  }
  if (seriesReminder == null) return "This date has its own reminder.";
  return `This date has its own reminder. Choose “${reminderLabel(seriesReminder)}” to use your series reminder again.`;
}

// What the detail of an occurrence-only guest offers (mirrors iOS
// OccurrenceOnlyDetailLayout). It reads as a standalone event:
//   rsvpStatus        "Invited" / "Declined" shown in the Your RSVP card, or
//                     null to hide the card (going: nothing to answer)
//   answers           a pending invitation's ["declined", "going"]; [] for a
//                     declined or canceled date (read-only here)
//   offersRemoveEvent Remove Event (DELETE /event-occurrences/{id}/rsvp/):
//                     going, and not canceled
export function occurrenceOnlyDetailLayout(occurrence) {
  const status = occurrenceParticipation(occurrence)?.status ?? null;
  const canceled = isCanceled(occurrence);
  const isGoing = status === "going";
  let rsvpStatus = null;
  if (status === "invited") rsvpStatus = "Invited";
  else if (status === "declined") rsvpStatus = "Declined";
  return {
    rsvpStatus,
    answers: status === "invited" && !canceled ? ["declined", "going"] : [],
    offersRemoveEvent: isGoing && !canceled,
  };
}

// Remove Event's confirmation (iOS OccurrenceOnlyDetailLayout.removeConfirmation).
export const REMOVE_EVENT_CONFIRMATION = {
  title: "Remove Event?",
  message: "This event will be removed from your calendar.",
  confirmLabel: "Remove Event",
  dismissLabel: "Cancel",
};
